import { FunctionComponent } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AuthLayout, ErrorPage } from "@/components";
import { ErrorPageType, PermissionRoles } from "@/enum";
import { useAppSelector } from "@/redux/hooks";
import { PUBLIC_PAGES_AFTER_LOGIN, ROUTE_CONFIG } from "@/routes/config";

import { useHasPermissions } from "@/utils/useHasPermissions";

import { AcceptInvitation } from "./pages/AcceptInvitation/AcceptInvitation";
import { ChangePassword } from "./pages/ChangePassword/ChangePassword";
import CheckYourEmail from "./pages/CheckYourEmail/CheckYourEmail";
import { ForgotPassword } from "./pages/ForgotPassword/ForgotPassword";
import { Login } from "./pages/Login/Login";
import ScenarioJoining from "./pages/ScenarioJoining/ScenarioJoining";
import { TwoFactorAuth } from "./pages/TwoFactorAuth/TwoFactorAuth";
import { authSelector } from "./redux/auth.slice";
import { useGetKpiHasDataQuery } from "../Private/redux/kpi/kpi.api";
import { EmailOtpPage } from "./pages/EmailOtp/EmailOtp";
import { PrivacyPolicy } from "./pages/PrivacyPolicy/PrivacyPolicy";
import { pendingMcpAuthorize } from "./mcpReturn";

const { TFA, COURSES, KPI_DASHBOARD, EMAIL_OTP, PRIVACY_POLICY } = ROUTE_CONFIG;

export const Public: FunctionComponent = () => {
	const { user, otpToken, pendingEmailOtpUserId, token } = useAppSelector(authSelector);
	const { pathname } = useLocation();
	const storedOtpToken = sessionStorage.getItem("otpToken");
	const effectiveOtpToken = otpToken || storedOtpToken;

	const { hasPermissions } = useHasPermissions();

	const hasKpiPermission = hasPermissions([PermissionRoles.KPI_DASHBOARD]);
	const isFullyAuthenticated = !!user && !!token && !effectiveOtpToken && !pendingEmailOtpUserId;
	const { data: kpiHasData, isLoading: kpiLoading } = useGetKpiHasDataQuery(undefined, {
		skip: !isFullyAuthenticated || !hasKpiPermission,
	});

	const publicPages = PUBLIC_PAGES_AFTER_LOGIN.find((pp: string) => pathname.includes(pp));

	if (
		user &&
		effectiveOtpToken &&
		!pendingEmailOtpUserId &&
		pathname !== TFA &&
		pathname !== "/error-page"
	) {
		return <Navigate to={TFA} />;
	}

	if (user && pendingEmailOtpUserId && pathname !== EMAIL_OTP && pathname !== "/error-page") {
		return <Navigate to={EMAIL_OTP} />;
	}

	if (
		user &&
		!effectiveOtpToken &&
		!pendingEmailOtpUserId &&
		!publicPages &&
		pathname !== "/error-page"
	) {
		// Back to an MCP client's consent page the login interrupted (BCM360-383).
		const mcpReturn = pendingMcpAuthorize();

		if (mcpReturn) {
			return <Navigate to={mcpReturn} />;
		}

		if (hasKpiPermission && kpiLoading) {
			return null;
		}

		if (hasKpiPermission && kpiHasData?.hasData) {
			return <Navigate to={KPI_DASHBOARD} />;
		} else {
			return <Navigate to={COURSES} />;
		}
	}

	return (
		<Routes>
			<Route element={<Navigate to="/login" />} path="/" />
			<Route element={<AuthLayout />}>
				<Route element={<Login />} path="login" />
				<Route element={<TwoFactorAuth />} path="2fa" />
				<Route element={<AcceptInvitation />} path="/accept-invitation" />
				<Route element={<ChangePassword />} path="/change-password" />
				<Route element={<ForgotPassword />} path="/forgot-password" />
				<Route element={<ScenarioJoining />} path="/first-login" />
				<Route element={<CheckYourEmail />} path="/check-email" />
				<Route element={<EmailOtpPage />} path="/email-otp" />
				<Route element={<PrivacyPolicy />} path={PRIVACY_POLICY} />
			</Route>
			<Route element={<ErrorPage type={ErrorPageType.NotFound} />} path="*" />
			<Route element={<ErrorPage type={ErrorPageType.Forbidden} />} path="/error-page" />
		</Routes>
	);
};
